import React, { useState, useContext, useRef, useEffect } from 'react';
import { AuthContext } from '../App';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import SEOHead from './SEOHead';
import rehypeRaw from 'rehype-raw';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Download, Send, User, Bot, Brain, Target } from 'lucide-react';
import { toast } from 'sonner';
import MermaidDiagram from './MermaidDiagram';
import html2pdf from 'html2pdf.js';
import LowCreditModal from './LowCreditModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CareerAmbition = () => {
  const { user, openAuth, fetchUser } = useContext(AuthContext);
  const location = useLocation();
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('initial');
  const [creditsUsed, setCreditsUsed] = useState(0);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [showLowCreditModal, setShowLowCreditModal] = useState(false);
  const [modalUserData, setModalUserData] = useState(null);
  const [pendingDownloadMessage, setPendingDownloadMessage] = useState(null);
  const [initialAmbitionProcessed, setInitialAmbitionProcessed] = useState(false);
  const [shouldAutoSubmit, setShouldAutoSubmit] = useState(false);  // NEW: Flag for auto-submit
  const messagesEndRef = useRef(null);
  const lastAiMessageRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToLatestAiMessage = () => {
    // Scroll to top of latest AI message (where banner is) instead of bottom
    if (lastAiMessageRef.current) {
      lastAiMessageRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    // Check if last message is AI message with complete response
    const lastMessage = messages[messages.length - 1];
    if (lastMessage && lastMessage.sender === 'ai' && lastMessage.type === 'complete') {
      // Scroll to top of AI response to show banner
      scrollToLatestAiMessage();
    } else {
      // For regular messages, scroll to bottom
      scrollToBottom();
    }
  }, [messages]);

  // Auto-focus input field
  useEffect(() => {
    if (!loading && inputRef.current) {
      inputRef.current.focus();
    }
  }, [loading, messages]);

  // Handle initial ambition from Dashboard navigation - SET INPUT ONLY
  useEffect(() => {
    const initialAmbition = location.state?.initialAmbition;
    
    if (initialAmbition && !initialAmbitionProcessed && user) {
      console.log('🎯 Received initial ambition from Dashboard:', initialAmbition);
      
      // Check if profile is complete
      if (!user?.profile?.completed_questionnaire) {
        toast.error('Please complete your profile first to get career guidance');
        setInputText(initialAmbition);  // Still set the text so user can submit after completing profile
        setInitialAmbitionProcessed(true);
        return;
      }
      
      // Set the input text
      setInputText(initialAmbition);
      setInitialAmbitionProcessed(true);
      
      // Set flag to trigger auto-submit
      setShouldAutoSubmit(true);
      console.log('✅ Auto-submit flag set. Will submit in 1 second...');
    }
  }, [location.state, user, initialAmbitionProcessed]);
  
  // Separate useEffect for auto-submit - runs when shouldAutoSubmit flag is set
  useEffect(() => {
    if (shouldAutoSubmit && inputText && !loading && user?.profile?.completed_questionnaire) {
      console.log('🚀 Auto-submitting ambition:', inputText);
      
      const timer = setTimeout(() => {
        // Trigger the same handler as manual send button
        sendMessage();
        setShouldAutoSubmit(false);  // Reset flag
        console.log('✅ Auto-submit completed!');
      }, 1000);  // 1 second delay for reliable rendering
      
      return () => clearTimeout(timer);
    }
  }, [shouldAutoSubmit, inputText, loading, user]);

  // Cleanup: Reset session when user leaves the page or closes window
  useEffect(() => {
    // Handler for window/tab close or navigation
    const handleBeforeUnload = () => {
      console.log('🔄 Career Ambition session closed - Window/tab closing');
    };

    // Add event listener for window close
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup function runs when component unmounts (user navigates away)
    return () => {
      console.log('🔄 Career Ambition session closed - User left the page');
      // Remove event listener
      window.removeEventListener('beforeunload', handleBeforeUnload);
      
      // Clear all session data when user leaves
      setMessages([]);
      setInputText('');
      setLoading(false);
      setStep('initial');
      setCreditsUsed(0);
      setProgress(0);
      setProgressMessage('');
      setShowLowCreditModal(false);
      setModalUserData(null);
      setPendingDownloadMessage(null);
    };
  }, []); // Empty dependency array means this runs only on mount/unmount

  // Function to send message with specific text (used for auto-submit from Dashboard)
  const sendMessageWithText = async (messageText) => {
    if (!messageText.trim() || loading) return;

    if (!user) {
      openAuth('register');
      return;
    }

    if (!user?.profile?.completed_questionnaire) {
      toast.error('Please complete your profile first');
      return;
    }

    const userMessage = {
      id: Date.now(),
      text: messageText,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setLoading(true);
    
    // Start progress simulation
    let progressInterval;
    setProgress(0);
    setProgressMessage('🚀 Initializing career analysis...');
    
    const progressSteps = [
      { percent: 15, message: '🔍 Analyzing your qualifications and experience...' },
      { percent: 35, message: '🎯 Identifying career pathways and opportunities...' },
      { percent: 55, message: '🌟 Researching role models and success stories...' },
      { percent: 75, message: '📚 Crafting personalized learning roadmap...' },
      { percent: 90, message: '📊 Creating career progression flowchart...' },
      { percent: 100, message: '✨ Finalizing your career roadmap...' }
    ];
    
    let currentStep = 0;
    progressInterval = setInterval(() => {
      if (currentStep < progressSteps.length) {
        setProgress(progressSteps[currentStep].percent);
        setProgressMessage(progressSteps[currentStep].message);
        currentStep++;
      }
    }, 5000);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/ai/career-ambition`,
        { message: messageText },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (progressInterval) {
        clearInterval(progressInterval);
        setProgress(100);
        setProgressMessage('✅ Career roadmap complete!');
        setTimeout(() => {
          setProgress(0);
          setProgressMessage('');
        }, 2000);
      }

      const data = response.data;
      
      const aiMessage = {
        id: Date.now() + 1,
        text: data.message,
        sender: 'ai',
        timestamp: new Date(),
        type: data.type,
        role_model_images: data.role_model_images || [],
        images: data.images || [],
        imageGenerationStatus: data.image_generation_status || 'none',
        mermaidDiagrams: data.mermaid_diagrams || [],
        downloadable: data.downloadable || false,
        creditsUsed: data.credits_used,
        creditsRemaining: data.credits_remaining,
        session_id: data.session_id
      };

      setMessages(prev => [...prev, aiMessage]);
      
      if (data.image_generation_status === 'generating' && data.session_id) {
        pollForImage(data.session_id, aiMessage.id);
      }
      setCreditsUsed(prev => prev + data.credits_used);
      setStep(data.step || 'completed');
      
    } catch (error) {
      console.error('Career ambition query failed:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to get response';
      
      const aiMessage = {
        id: Date.now() + 1,
        text: errorMessage,
        sender: 'ai',
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, aiMessage]);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
      setProgress(0);
      setProgressMessage('');
    }
  };

  const sendMessage = async () => {
    if (!inputText.trim() || loading) return;

    if (!user) {
      openAuth('register');
      return;
    }

    if (!user?.profile?.completed_questionnaire) {
      toast.error('Please complete your profile first');
      return;
    }

    const userMessage = {
      id: Date.now(),
      text: inputText,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const currentQuery = inputText;
    setInputText('');
    setLoading(true);
    
    // Start progress simulation - show during full career roadmap generation
    let progressInterval;
    
    // Always start progress tracking for long operations
    setProgress(0);
    setProgressMessage('🚀 Initializing career analysis...');
    
    const progressSteps = [
      { percent: 15, message: '🔍 Analyzing your qualifications and experience...' },
      { percent: 35, message: '🎯 Identifying career pathways and opportunities...' },
      { percent: 55, message: '🌟 Researching role models and success stories...' },
      { percent: 75, message: '📚 Crafting personalized learning roadmap...' },
      { percent: 90, message: '📊 Creating career progression flowchart...' },
      { percent: 100, message: '✨ Finalizing your career roadmap...' }
    ];
    
    let currentStep = 0;
    progressInterval = setInterval(() => {
      if (currentStep < progressSteps.length) {
        setProgress(progressSteps[currentStep].percent);
        setProgressMessage(progressSteps[currentStep].message);
        currentStep++;
      }
    }, 5000); // Update every 5 seconds

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/ai/career-ambition`,
        { message: currentQuery },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      // Clear progress interval
      if (progressInterval) {
        clearInterval(progressInterval);
        setProgress(100);
        setProgressMessage('✅ Career roadmap complete!');
        setTimeout(() => {
          setProgress(0);
          setProgressMessage('');
        }, 2000);
      }

      const data = response.data;
      
      // Create AI message
      const aiMessage = {
        id: Date.now() + 1,
        text: data.message,
        sender: 'ai',
        timestamp: new Date(),
        type: data.type,
        role_model_images: data.role_model_images || [],
        images: data.images || [],
        imageGenerationStatus: data.image_generation_status || 'none',  // Progressive loading
        mermaidDiagrams: data.mermaid_diagrams || [],
        downloadable: data.downloadable || false,
        creditsUsed: data.credits_used,
        creditsRemaining: data.credits_remaining,
        session_id: data.session_id  // For Word document download
      };

      console.log('Career Ambition Response:', {
        role_model_images: aiMessage.role_model_images.length,
        images: aiMessage.images.length,
        imageGenerationStatus: aiMessage.imageGenerationStatus,
        mermaidDiagrams: aiMessage.mermaidDiagrams.length,
        diagrams: aiMessage.mermaidDiagrams,
        textLength: aiMessage.text.length,
        textPreview: aiMessage.text.substring(0, 500),
        hasHTMLImages: aiMessage.text.includes('<img'),
        responseType: data.type
      });
      
      // Debug PDF issue - check if text is actually there
      if (data.type === 'complete' && aiMessage.text.length < 100) {
        console.error('⚠️ PDF ISSUE: Response text is too short!', {
          textLength: aiMessage.text.length,
          fullText: aiMessage.text
        });
      }

      setMessages(prev => [...prev, aiMessage]);
      
      // PROGRESSIVE LOADING: Poll for image if generating
      if (data.image_generation_status === 'generating' && data.session_id) {
        pollForImage(data.session_id, aiMessage.id);
      }
      setCreditsUsed(prev => prev + data.credits_used);
      setStep(data.step || 'completed');
      
    } catch (error) {
      console.error('Career ambition query failed:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to get response';
      
      const aiMessage = {
        id: Date.now() + 1,
        text: errorMessage,
        sender: 'ai',
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, aiMessage]);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
      setProgress(0);
      setProgressMessage('');
    }
  };

  // PROGRESSIVE LOADING: Poll for image generation completion
  const pollForImage = async (sessionId, messageId) => {
    const maxAttempts = 24; // 24 attempts * 2.5s = 60 seconds max
    let attempts = 0;
    
    const checkStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API}/ai/career-ambition/image-status/${sessionId}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        
        const { status, images } = response.data;
        
        if (status === 'completed' && images.length > 0) {
          // Update the message with the generated images
          setMessages(prev => prev.map(msg => 
            msg.id === messageId 
              ? { ...msg, images, imageGenerationStatus: 'completed' }
              : msg
          ));
          console.log('✅ Banner image loaded:', images);
          return true; // Stop polling
        } else if (status === 'failed') {
          console.error('❌ Image generation failed');
          setMessages(prev => prev.map(msg => 
            msg.id === messageId 
              ? { ...msg, imageGenerationStatus: 'failed' }
              : msg
          ));
          return true; // Stop polling
        }
        
        // Still generating, continue polling
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(checkStatus, 2500); // Check every 2.5 seconds
        } else {
          console.warn('⏱️ Image generation timeout - stopped polling');
          setMessages(prev => prev.map(msg => 
            msg.id === messageId 
              ? { ...msg, imageGenerationStatus: 'timeout' }
              : msg
          ));
        }
      } catch (error) {
        console.error('Failed to check image status:', error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(checkStatus, 2500);
        }
      }
    };
    
    // Start polling after a brief delay
    setTimeout(checkStatus, 1000);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const downloadImage = (imageUrl, index) => {
    const link = document.createElement('a');
    link.href = `${BACKEND_URL}${imageUrl}`;
    link.download = `career_visual_${index + 1}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleRechargeSuccess = async () => {
    // Called after successful payment - auto-trigger download
    toast.success('Recharge successful! Starting download...');
    
    // Refresh user data to get updated credits
    if (fetchUser) {
      await fetchUser();
    }
    
    // Trigger download if there's a pending message
    if (pendingDownloadMessage) {
      // Use setTimeout to ensure modal closes and user data is refreshed
      setTimeout(() => {
        performDownload(pendingDownloadMessage);
        setPendingDownloadMessage(null);
      }, 500);
    }
  };

  const performDownload = async (message) => {
    // Actual download logic without credit check
    if (!message.session_id) {
      toast.error('Session ID not found. Please regenerate your career roadmap.');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/career-ambition/download/${message.session_id}`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'Nerd_AI_Career_Roadmap.docx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Career roadmap downloaded successfully! 30 credits deducted.');
      
      // Refresh user data to update credits
      if (fetchUser) {
        await fetchUser();
      }
    } catch (error) {
      console.error('Download error:', error);
      
      // Check if it's a 402 error (insufficient credits)
      if (error.response?.status === 402) {
        toast.error(error.response?.data?.detail || 'Insufficient credits for download');
      } else {
        toast.error('Failed to download. Please try again.');
      }
    }
  };

  const downloadResponse = async (message) => {
    // Download as Word document from backend - Costs 30 credits
    if (!message.session_id) {
      toast.error('Session ID not found. Please regenerate your career roadmap.');
      return;
    }
    
    // Check if user has enough credits (30 credits required)
    const DOWNLOAD_COST = 30;
    const isAdmin = user?.email === 'shifracs@gmail.com';
    
    // Fetch fresh user data to get latest credit balance
    let freshUserData = null;
    let currentCredits = user?.nerd_ai_credits || 0;
    
    if (fetchUser) {
      try {
        freshUserData = await fetchUser();
        currentCredits = freshUserData?.nerd_ai_credits || user?.nerd_ai_credits || 0;
      } catch (error) {
        console.error('Failed to fetch fresh user data:', error);
        currentCredits = user?.nerd_ai_credits || 0;
        freshUserData = user;
      }
    }
    
    if (!isAdmin && currentCredits < DOWNLOAD_COST) {
      // Store fresh user data for modal to display correct balance
      setModalUserData(freshUserData || user);
      
      // Store the message for download after recharge
      setPendingDownloadMessage(message);
      
      // Show low credit modal with fresh data
      setShowLowCreditModal(true);
      toast.error(`Insufficient credits. You need ${DOWNLOAD_COST} credits to download. Current balance: ${currentCredits}`);
      return;
    }
    
    // User has enough credits, proceed with download
    await performDownload(message);
  };

  return (
    <>
      <SEOHead title="Career Ambition AI" description="Map your career ambitions with AI-generated visual roadmaps, personalised study plans and step-by-step guidance from Nerd Nation." path="/career-ambition" />
      <div className="chat-viewport fixed top-0 left-0 right-0 bg-gray-50 flex flex-col overflow-hidden" style={{ touchAction: 'none' }}>
        {/* Scrollable Messages Area */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden pt-20 min-h-0" style={{ paddingBottom: 'calc(5rem + env(safe-area-inset-bottom, 0px))', touchAction: 'pan-y' }}>
          <div className="max-w-4xl mx-auto px-4">
            {/* Chat Messages */}
            <div className="py-6" data-testid="career-messages">
              {messages.length === 0 ? (
                <div className="text-center py-16">
                  {/* Empty state - no content */}
                </div>
              ) : (
                <div className="space-y-6">
                  {messages.map((message, index) => (
                    <div
                      key={message.id}
                      ref={message.sender === 'ai' && index === messages.length - 1 ? lastAiMessageRef : null}
                      className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`flex items-start space-x-3 max-w-[90%] md:max-w-3xl ${message.sender === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                        {message.sender === 'user' ? (
                          <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-gradient-to-r from-amber-600 to-orange-600">
                            <User className="h-4 w-4 text-white" />
                          </div>
                        ) : (
                          <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
                            <img 
                              src="https://customer-assets.emergentagent.com/job_736cdda7-e974-4fad-aa62-3a758e69e883/artifacts/hc6xcw6c_Time%282%29.png" 
                              alt="Nerd AI"
                              className="w-full h-full object-contain" loading="lazy" decoding="async"/>
                          </div>
                        )}
                        
                        <div className={`px-4 py-3 rounded-2xl min-w-0 flex-1 overflow-hidden ${
                          message.sender === 'user'
                            ? 'bg-gradient-to-r from-amber-600 to-orange-600 text-white'
                            : message.isError 
                            ? 'bg-red-50 border-2 border-red-300' 
                            : 'bg-white border-2 border-gray-200 text-gray-900'
                        }`}>
                          {message.sender === 'user' ? (
                            <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{message.text}</p>
                          ) : (
                            <div className="text-sm leading-relaxed max-w-full overflow-hidden">
                              {/* Colorful Ribbon Banner with NERD AI Branding - Mobile Responsive */}
                              {message.type === 'complete' && (
                                <div className="mb-8 rounded-xl overflow-hidden shadow-2xl career-ribbon-banner">
                                  <div className="relative h-32 sm:h-40 bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 overflow-hidden">
                                    {/* Animated background pattern */}
                                    <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/10 to-transparent animate-pulse"></div>
                                    
                                    {/* Decorative circles */}
                                    <div className="absolute top-0 left-10 w-20 h-20 bg-white/20 rounded-full blur-xl"></div>
                                    <div className="absolute bottom-0 right-20 w-32 h-32 bg-yellow-300/20 rounded-full blur-2xl"></div>
                                    <div className="absolute top-10 right-10 w-16 h-16 bg-white/10 rounded-full blur-lg"></div>
                                    
                                    {/* Content */}
                                    <div className="relative h-full flex flex-col items-center justify-center text-white px-3 py-3 sm:px-4 sm:py-4">
                                      {/* Nerd Nation Branding */}
                                      <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                                        <div className="w-10 h-10 sm:w-14 sm:h-14 bg-white rounded-full p-1.5 sm:p-2 shadow-lg flex-shrink-0">
                                          <img 
                                            src="https://customer-assets.emergentagent.com/job_736cdda7-e974-4fad-aa62-3a758e69e883/artifacts/hc6xcw6c_Time%282%29.png" 
                                            alt="Nerd Nation"
                                            className="w-full h-full object-contain" loading="lazy" decoding="async"/>
                                        </div>
                                        <div className="text-left">
                                          <h1 className="text-xl sm:text-3xl md:text-4xl font-black tracking-tight text-white" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
                                            Nerd Nation
                                          </h1>
                                          <p className="text-[10px] sm:text-xs md:text-sm text-white/90 font-medium tracking-wide">Personalized Roadmap to Success</p>
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {/* Bottom wave decoration */}
                                    <svg className="absolute bottom-0 left-0 w-full" viewBox="0 0 1200 60" preserveAspectRatio="none">
                                      <path d="M0,30 Q300,60 600,30 T1200,30 L1200,60 L0,60 Z" fill="white" fillOpacity="0.2"/>
                                    </svg>
                                  </div>
                                </div>
                              )}
                              
                              <div className="prose prose-sm max-w-none">
                                <ReactMarkdown
                                  rehypePlugins={[rehypeRaw]}
                                  components={{
                                  p: ({ node, children }) => <p className="mb-3 last:mb-0 break-words overflow-wrap-anywhere leading-relaxed">{children}</p>,
                                  a: ({ node, children, href }) => <a href={href} className="text-gold-primary hover:text-orange-700 underline break-words" target="_blank" rel="noopener noreferrer">{children}</a>,
                                  h1: ({ node, children }) => <h1 className="text-lg font-bold text-gray-900 mt-4 mb-3 break-words">{children}</h1>,
                                  h2: ({ node, children }) => {
                                    // Add colorful separators and styling for major sections
                                    const childText = children && children.toString();
                                    const isRoleModelSection = childText.includes('Inspiring Role Models');
                                    const isRoadmapSection = childText.includes('Complete Career Roadmap');
                                    const isSkillsSection = childText.includes('Essential Skills');
                                    const isResourcesSection = childText.includes('Resources') || childText.includes('Learning Path');
                                    
                                    // Determine separator color based on section
                                    let separatorColor = 'from-orange-400 to-red-400';
                                    if (isRoleModelSection) separatorColor = 'from-amber-400 to-yellow-400';
                                    if (isRoadmapSection) separatorColor = 'from-blue-400 to-purple-400';
                                    if (isSkillsSection) separatorColor = 'from-green-400 to-teal-400';
                                    if (isResourcesSection) separatorColor = 'from-pink-400 to-rose-400';
                                    
                                    return (
                                      <>
                                        {/* Colorful separator before section */}
                                        <div className="my-6">
                                          <div className={`h-1 bg-gradient-to-r ${separatorColor} rounded-full`}></div>
                                          <div className="h-0.5 bg-gradient-to-r from-transparent via-gray-300 to-transparent mt-1"></div>
                                        </div>
                                        <h2 className={`text-lg font-bold text-gray-900 mt-4 mb-3 break-words flex items-center gap-2 ${
                                          isRoleModelSection ? 'text-amber-600' : 
                                          isRoadmapSection ? 'text-blue-600' :
                                          isSkillsSection ? 'text-green-600' : ''
                                        }`}>
                                          {children}
                                        </h2>
                                      </>
                                    );
                                  },
                                  h3: ({ node, children }) => {
                                    // Add golden star icon for role model headings and spacing for phase sections
                                    const childText = children && children.toString();
                                    const isRoleModel = childText.includes('Role Model');
                                    const isPhase = childText.includes('Phase');
                                    
                                    return (
                                      <>
                                        {/* Add subtle separator for Phase sections */}
                                        {isPhase && (
                                          <div className="mt-6 mb-3">
                                            <div className="h-px bg-gradient-to-r from-transparent via-orange-300 to-transparent"></div>
                                          </div>
                                        )}
                                        <h3 className={`text-base font-semibold text-white ${isPhase ? 'mt-4' : 'mt-3'} mb-2 break-words flex items-center gap-2`}>
                                          {isRoleModel && (
                                            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 shadow-lg">
                                              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                              </svg>
                                            </span>
                                          )}
                                          {children}
                                        </h3>
                                      </>
                                    );
                                  },
                                  ul: ({ node, children }) => <ul className="list-disc ml-5 mb-3 space-y-1 break-words">{children}</ul>,
                                  ol: ({ node, children }) => <ol className="list-decimal ml-5 mb-3 space-y-1 break-words">{children}</ol>,
                                  li: ({ node, children }) => <li className="break-words overflow-wrap-anywhere leading-relaxed">{children}</li>,
                                  strong: ({ node, children }) => <strong className="font-bold text-gray-900">{children}</strong>,
                                  em: ({ node, children }) => <em className="italic">{children}</em>,
                                  code: ({ node, inline, children }) => 
                                    inline 
                                      ? <code className="bg-gray-200 px-1.5 py-0.5 rounded text-xs font-mono break-all">{children}</code>
                                      : <pre className="bg-gray-200 p-3 rounded text-xs font-mono overflow-x-auto my-3 break-all"><code>{children}</code></pre>,
                                  blockquote: ({ node, children }) => <blockquote className="border-l-4 border-orange-300 pl-4 italic my-3 break-words">{children}</blockquote>,
                                  hr: ({ node }) => <hr className="my-4 border-t border-gray-300" />,
                                  }}
                                >
                                  {message.text}
                                </ReactMarkdown>
                              </div>
                              
                              {/* Role model images - show if they exist and weren't shown inline */}
                              {message.role_model_images && message.role_model_images.length > 0 && !message.text.includes('<img') && (
                                <div className="mt-6 p-4 bg-orange-50 rounded-lg border-2 border-orange-200">
                                  <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                    <Target className="h-4 w-4 text-gold-primary" />
                                    Role Models to Inspire You
                                  </h4>
                                  <div className="flex flex-wrap justify-center gap-6">
                                    {message.role_model_images.map((imageUrl, idx) => (
                                      <div key={idx} className="flex flex-col items-center">
                                        <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-orange-400 bg-white shadow-lg">
                                          <img 
                                            src={imageUrl}
                                            alt={`Role Model ${idx + 1}`}
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                              e.target.src = 'https://ui-avatars.com/api/?name=Role+Model&size=200&background=f97316&color=fff&bold=true';
                                            }} loading="lazy" decoding="async" />
                                        </div>
                                        <p className="text-xs text-gray-600 mt-2">Role Model {idx + 1}</p>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {/* Milestone/Infographic Images - Display after mermaid */}
                              {message.images && message.images.length > 1 && (
                                <div className="mt-6">
                                  <div className="bg-gradient-to-br from-orange-50 to-red-50 p-4 rounded-lg border-2 border-orange-200">
                                    <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                      📊 Career Progression Visual
                                    </h4>
                                    <div className="relative rounded-lg overflow-hidden shadow-lg">
                                      <img 
                                        src={`${BACKEND_URL}${message.images[1]}`}
                                        alt="Career Milestones Infographic"
                                        className="w-full h-auto" loading="lazy" decoding="async"/>
                                      <Button
                                        onClick={() => downloadImage(message.images[1], 1)}
                                        className="absolute top-2 right-2 bg-white/90 hover:bg-white text-gold-primary px-3 py-1 text-xs"
                                        size="sm"
                                      >
                                        <Download className="h-3 w-3 mr-1" />
                                        Download
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              )}
                              
                              {/* Display Mermaid diagrams */}
                              {message.mermaidDiagrams && message.mermaidDiagrams.length > 0 && (
                                <div className="mt-4 space-y-4">
                                  {message.mermaidDiagrams.map((diagram, idx) => (
                                    <div key={idx} className="bg-white rounded-lg border-2 border-orange-200 p-4">
                                      <MermaidDiagram 
                                        chart={diagram}
                                        className="flex justify-center"
                                      />
                                    </div>
                                  ))}
                                </div>
                              )}
                              
                              {/* Download and Credit info for complete responses */}
                              {message.type === 'complete' && (
                                <div className="mt-4 pt-3 border-t border-white/10 space-y-3">
                                  <div className="flex items-center justify-center">
                                    <Button
                                      onClick={() => downloadResponse(message)}
                                      className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-blue-800 hover:to-blue-900 text-white px-3 sm:px-6 py-2 text-xs sm:text-sm"
                                    >
                                      <Download className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                                      <span className="hidden sm:inline">Download Career Roadmap (30 Credits)</span>
                                      <span className="sm:hidden">Download (30 Credits)</span>
                                    </Button>
                                  </div>
                                  <div className="flex items-center justify-between text-xs text-gray-500">
                                    <span>Credits used: {message.creditsUsed}</span>
                                    <span>Remaining: {message.creditsRemaining}</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {loading && (
                    <div className="flex justify-start">
                      <div className="flex items-start space-x-3 max-w-[90%] md:max-w-3xl w-full">
                        <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
                          <img 
                            src="https://customer-assets.emergentagent.com/job_736cdda7-e974-4fad-aa62-3a758e69e883/artifacts/hc6xcw6c_Time%282%29.png" 
                            alt="Nerd AI"
                            className="w-full h-full object-contain" loading="lazy" decoding="async"/>
                        </div>
                        <div className="bg-white border-2 border-gray-200 rounded-2xl px-4 py-4 flex-1">
                          {progress > 0 ? (
                            <div className="space-y-3">
                              <p className="text-sm text-gray-800 font-medium">{progressMessage}</p>
                              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                                <div 
                                  className="bg-gradient-to-r from-violet-500 via-purple-500 to-pink-500 h-3 rounded-full transition-all duration-500 ease-out"
                                  style={{ width: `${progress}%` }}
                                >
                                  <div className="h-full w-full flex items-center justify-end pr-2">
                                    <span className="text-[10px] text-white font-bold">{progress}%</span>
                                  </div>
                                </div>
                              </div>
                              <p className="text-xs text-gray-600 italic text-center mt-2">
                                Creating a Superb Career Roadmap for You (Takes 30 to 45 Seconds)
                              </p>
                            </div>
                          ) : (
                            <div className="flex gap-1">
                              <div className="w-2 h-2 bg-gray-600 rounded-full animate-bounce"></div>
                              <div className="w-2 h-2 bg-gray-600 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                              <div className="w-2 h-2 bg-gray-600 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        <div className="max-w-4xl mx-auto px-2 sm:px-4 py-2 sm:py-3">
          <div className="flex gap-1 sm:gap-2">
            <Input
              ref={inputRef}
              type="text"
              placeholder="Type your career ambition here..."
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={loading}
              className="flex-1 border-2 border-orange-200 focus:border-orange-500 text-sm sm:text-base h-10 sm:h-auto bg-white text-gray-900 placeholder:text-gray-500"
              data-testid="career-ambition-input"
            />
            <Button
              onClick={sendMessage}
              disabled={loading || !inputText.trim()}
              className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white px-3 sm:px-6 h-10 sm:h-auto disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="send-message-button"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
      
      {/* Low Credit Modal */}
      {showLowCreditModal && (
        <LowCreditModal 
          user={modalUserData || user} 
          forceShow={true}
          context="download"
          onClose={() => {
            setShowLowCreditModal(false);
            setModalUserData(null);
            setPendingDownloadMessage(null);
          }}
          onRechargeSuccess={handleRechargeSuccess}
        />
      )}
    </>
  );
};

export default CareerAmbition;
